import prettyMilliseconds from "pretty-ms";
import React, { ReactElement, useState, useEffect } from "react";
import {
    BsCheckCircle, BsClockHistory, BsFillExclamationTriangleFill,
} from "react-icons/bs";
import TimeAgo from "react-timeago";
import {
    OverlayTrigger, Popover, Badge,
} from "react-bootstrap";
import {
    DueDateType,
    TicketDueDate,
} from "../../__generated__/api";
import Styles from "./TicketDueDates.module.scss";

interface TicketTasksProps {
    dueDates: TicketDueDate[];
    isDueDateHistoryExpanded: boolean;
}
const Titles = {
    [DueDateType.SlaFirstResponse]: "First response",
    [DueDateType.SlaTimeToUpdate]: "Next update",
    [DueDateType.SlaRequesterWaitTime]: "Requester Wait time",

};
function TicketDueDatesRow({ ticketDueDate, isDueDateHistoryExpanded }: {
    ticketDueDate: TicketDueDate;
    isDueDateHistoryExpanded: boolean;
}): ReactElement | null {
    const dueDate = new Date(ticketDueDate.dueDate);

    const completedDate = ticketDueDate.completedDate ? new Date(ticketDueDate.completedDate) : undefined;
    const startedDate = ticketDueDate.startedDate ? new Date(ticketDueDate.startedDate) : undefined;

    const [isInViolation, setIsInViolation] = useState(dueDate.getTime() < (completedDate?.getTime() ?? Date.now()));

    const shouldDisplay = isDueDateHistoryExpanded || !ticketDueDate.completedDate;
    useEffect(() => {
        if (!completedDate) {
            const timeLeft = dueDate.getTime() - Date.now();
            if (timeLeft > 0) {
                const timeout = setTimeout(() => {
                    setIsInViolation(!ticketDueDate.completedDate && dueDate.getTime() < Date.now());
                }, timeLeft);

                return () => {
                    clearTimeout(timeout);
                };
            }
        }
        return undefined;
    }, [!completedDate && shouldDisplay]);

    if (!shouldDisplay) {
        return null;
    }

    let humanizedDate;
    if (completedDate) {
        const start = new Date(ticketDueDate.startedDate ?? Date.now());
        const slaTime = prettyMilliseconds(dueDate.getTime() - start.getTime(), { verbose: true });
        const duration = prettyMilliseconds(completedDate.getTime() - start.getTime(), {
            secondsDecimalDigits: 0,
        });
        humanizedDate = <span title={`Took ${duration}, the SLA was: ${slaTime}`}>{duration}</span>;
    }

    let icon : ReactElement | undefined;
    let pill : ReactElement | undefined;
    let color: string;

    if (isInViolation && !completedDate) {
        icon = <BsFillExclamationTriangleFill className="text-danger" title="SLA violated" />;
        color = "text-danger";
        pill = <Badge bg="danger">SLA: Ongoing Violation</Badge>;
    } else if (isInViolation && completedDate) {
        icon = <BsCheckCircle className="text-danger-light" title="Done" />;
        color = "text-danger-light";
        pill = <Badge bg="danger-light" className="text-primary">SLA: Remedied Violation</Badge>;
    } else if (!isInViolation && completedDate) {
        icon = <BsCheckCircle className="text-success" title="Done" />;
        color = "text-success";
        pill = <Badge bg="success">SLA: Fulfilled</Badge>;
    } else {
        icon = <BsClockHistory />;
        color = "";
        pill = <Badge bg="info">SLA: Pending</Badge>;
    }

    const slaStatusPopover = (
        <Popover className={`${Styles["popover-sla-status"]}`}>
            <Popover.Body>
                <div className="d-flex flex-column gap-2">
                    <div className={`d-flex flex-row justify-content-between align-items-center ${color} mb-2`}>
                        <span className="me-2">
                            {icon}
                        </span>
                        <span className="text-start flex-grow-1 me-4 text-nowrap">
                            {Titles[ticketDueDate.type]}
                        </span>
                        <span>
                            {pill}
                        </span>
                    </div>
                    <div>
                        {startedDate && (
                            <div className="d-flex flex-row justify-content-between gap-2">
                                <span>Triggered:</span>
                                <span>{startedDate.toLocaleString()}</span>
                            </div>
                        )}
                        <div className={`d-flex flex-row justify-content-between gap-2
                            ${completedDate && !isInViolation ? "" : color} ${!completedDate || isInViolation ? "fw-bold" : ""}`}
                        >
                            <span>Due:</span>
                            <span>{dueDate.toLocaleString()}</span>
                        </div>
                        {completedDate && (
                            <div className={`d-flex flex-row justify-content-between gap-2
                                ${!isInViolation ? "text-success fw-medium" : ""}`}
                            >
                                <span>Completed:</span>
                                <span>{completedDate.toLocaleString()}</span>
                            </div>
                        )}
                    </div>
                </div>
            </Popover.Body>
        </Popover>
    );

    return (
        <div className="list-group-item px-0 ">
            <OverlayTrigger trigger={["click", "hover"]} placement="bottom" overlay={slaStatusPopover}>
                <div>
                    <small className={`d-flex justify-content-between w-100 align-items-center ${color}`}>
                        <span className="me-2">
                            {icon}
                        </span>
                        <span className="text-start flex-grow-1">
                            {Titles[ticketDueDate.type]}
                        </span>
                        <span>
                            {humanizedDate ?? (
                                <TimeAgo date={dueDate} title={dueDate.toLocaleString()} />
                            )}
                        </span>

                    </small>
                </div>
            </OverlayTrigger>

        </div>
    );
}

export default function TicketDueDates({
    dueDates,
    isDueDateHistoryExpanded,
}: TicketTasksProps): ReactElement {
    return (
        <div className="list-group list-group-flush border-bottom">
            {dueDates.map(dueDate => (
                <TicketDueDatesRow
                    ticketDueDate={dueDate}
                    key={dueDate.type + dueDate.dueDate + dueDate.completedDate}
                    isDueDateHistoryExpanded={isDueDateHistoryExpanded}
                />
            ))}
        </div>
    );
}
